import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Box,
  IconButton,
  MenuItem,
  Select,
  Slider,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import * as fabric from 'fabric'

import { Icon } from '~components/icons'
import { FileFetchError, fetchFileBlob } from '~utils/fileUtils'
import { MAX_MARKUP_PDF_PAGES, getPdfDocument } from '~utils/pdfUtils'

import './DocumentMarkup.scss'

import { Button } from '.'

const MAX_PAGE_PIXELS = 4_000_000
const MAX_RENDER_SCALE = 5
const MAX_HISTORY = 50

type MarkupTool = 'select' | 'pen' | 'rectangle' | 'circle' | 'text'

type MarkupPage = {
  background: HTMLCanvasElement | HTMLImageElement
  pixelWidth: number
  pixelHeight: number
  pointWidth: number
  pointHeight: number
}

type HistoryEntry = {
  pageIndex: number
  json: string
}

const fetchBlobUrl = async (url: string): Promise<string> => {
  const blob = await fetchFileBlob(url).catch((error: unknown) => {
    const status = error instanceof FileFetchError ? error.status : undefined
    throw new Error(`Failed to fetch image: ${status ?? 'network error'}`)
  })
  return URL.createObjectURL(blob)
}

const isPdfUrl = (url: string): boolean => {
  const cleanUrl = url.split('?')[0].toLowerCase()
  return cleanUrl.endsWith('.pdf')
}

const renderPdfPages = async (pdfBlobUrl: string): Promise<MarkupPage[] | null> => {
  const pdf = await getPdfDocument(pdfBlobUrl)
  try {
    if (pdf.numPages > MAX_MARKUP_PDF_PAGES) return null

    const pages: MarkupPage[] = []
    for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
      const page = await pdf.getPage(pageNumber)
      const baseViewport = page.getViewport({ scale: 1 })
      const maxScale = Math.sqrt(MAX_PAGE_PIXELS / (baseViewport.width * baseViewport.height))
      const viewport = page.getViewport({ scale: Math.min(MAX_RENDER_SCALE, maxScale) })

      const canvas = document.createElement('canvas')
      canvas.width = Math.floor(viewport.width)
      canvas.height = Math.floor(viewport.height)

      const context = canvas.getContext('2d')
      if (!context) {
        throw new Error('Could not get canvas context')
      }

      await page.render({
        canvasContext: context,
        viewport,
      }).promise

      pages.push({
        background: canvas,
        pixelWidth: canvas.width,
        pixelHeight: canvas.height,
        pointWidth: baseViewport.width,
        pointHeight: baseViewport.height,
      })
    }
    return pages
  } finally {
    pdf.destroy()
  }
}

const loadImagePage = async (imageBlobUrl: string): Promise<MarkupPage> => {
  const image = await fabric.util.loadImage(imageBlobUrl)
  const pixelWidth = image.naturalWidth || image.width || 1
  const pixelHeight = image.naturalHeight || image.height || 1
  return {
    background: image,
    pixelWidth,
    pixelHeight,
    pointWidth: pixelWidth,
    pointHeight: pixelHeight,
  }
}

const serializeCanvas = (canvas: fabric.Canvas): string =>
  JSON.stringify({ objects: canvas.toObject().objects })

const dataUrlToBlob = (dataUrl: string, type: string): Blob => {
  const byteString = atob(dataUrl.split(',')[1])
  const bytes = new Uint8Array(byteString.length)
  for (let index = 0; index < byteString.length; index++) {
    bytes[index] = byteString.charCodeAt(index)
  }
  return new Blob([bytes], { type })
}

const getOrientation = (page: MarkupPage): 'l' | 'p' =>
  page.pointWidth > page.pointHeight ? 'l' : 'p'

interface DocumentMarkupProps {
  imageUrl?: string
  onSave?: (blob: Blob) => void | Promise<void>
  width?: number
  height?: number
  disabled?: boolean
}

const COLORS = ['#000000', '#FF0000', '#0000FF', '#FFFFFF']

const DocumentMarkup: React.FC<DocumentMarkupProps> = ({
  imageUrl,
  onSave,
  width = 1100,
  height = 600,
  disabled = false,
}) => {
  const theme = useTheme()
  const canvasElementsRef = useRef<(HTMLCanvasElement | null)[]>([])
  const fabricCanvasesRef = useRef<fabric.Canvas[]>([])
  const backgroundImagesRef = useRef<fabric.FabricImage[]>([])
  const exportScalesRef = useRef<number[]>([])
  const activePageIndexRef = useRef(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const [pages, setPages] = useState<MarkupPage[]>([])
  const [fabricCanvases, setFabricCanvases] = useState<fabric.Canvas[]>([])
  const [activeTool, setActiveTool] = useState<MarkupTool>('pen')
  const [strokeColor, setStrokeColor] = useState(COLORS[0])
  const [strokeWidth, setStrokeWidth] = useState(3)
  const [canUndo, setCanUndo] = useState(false)
  const [canRedo, setCanRedo] = useState(false)
  const [isImageLoaded, setIsImageLoaded] = useState(false)
  const [isOverPageLimit, setIsOverPageLimit] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [selectedObject, setSelectedObject] = useState<fabric.FabricObject | null>(null)
  const [textFormat, setTextFormat] = useState({
    fontSize: 24,
    isBold: false,
    isItalic: false,
    isUnderline: false,
    isStrikethrough: false,
  })
  const [zoomLevel, setZoomLevel] = useState(1)

  const historyRef = useRef<HistoryEntry[]>([])
  const historyIndexRef = useRef(-1)
  const baseSnapshotsRef = useRef<string[]>([])
  const isLoadingHistoryRef = useRef(false)

  const getActiveCanvas = useCallback(
    (): fabric.Canvas | null => fabricCanvasesRef.current[activePageIndexRef.current] ?? null,
    [],
  )

  const updateHistoryButtons = useCallback((): void => {
    setCanUndo(historyIndexRef.current >= 0)
    setCanRedo(historyIndexRef.current < historyRef.current.length - 1)
  }, [])

  const saveToHistory = useCallback(
    (pageIndex: number): void => {
      const canvas = fabricCanvasesRef.current[pageIndex]
      if (isLoadingHistoryRef.current || !canvas) return

      historyRef.current = historyRef.current.slice(0, historyIndexRef.current + 1)
      historyRef.current.push({ pageIndex, json: serializeCanvas(canvas) })
      if (historyRef.current.length > MAX_HISTORY) {
        const droppedEntry = historyRef.current.shift()
        if (droppedEntry) {
          baseSnapshotsRef.current[droppedEntry.pageIndex] = droppedEntry.json
        }
      }
      historyIndexRef.current = historyRef.current.length - 1

      updateHistoryButtons()
    },
    [updateHistoryButtons],
  )

  const restorePage = useCallback(
    (pageIndex: number, json: string): void => {
      const canvas = fabricCanvasesRef.current[pageIndex]
      if (!canvas) return

      isLoadingHistoryRef.current = true
      canvas
        .loadFromJSON(json)
        .then(() => {
          canvas.backgroundImage = backgroundImagesRef.current[pageIndex]
          canvas.renderAll()
        })
        .finally(() => {
          isLoadingHistoryRef.current = false
          updateHistoryButtons()
        })
    },
    [updateHistoryButtons],
  )

  const handleUndo = useCallback((): void => {
    if (historyIndexRef.current < 0) return

    const { pageIndex } = historyRef.current[historyIndexRef.current]
    const previousEntry = historyRef.current
      .slice(0, historyIndexRef.current)
      .findLast((entry) => entry.pageIndex === pageIndex)
    historyIndexRef.current--
    restorePage(pageIndex, previousEntry?.json ?? baseSnapshotsRef.current[pageIndex])
  }, [restorePage])

  const handleRedo = useCallback((): void => {
    if (historyIndexRef.current >= historyRef.current.length - 1) return

    historyIndexRef.current++
    const { pageIndex, json } = historyRef.current[historyIndexRef.current]
    restorePage(pageIndex, json)
  }, [restorePage])

  useEffect(() => {
    if (!imageUrl) return

    setIsImageLoaded(false)
    setIsOverPageLimit(false)
    setPages([])

    let isCancelled = false
    let blobUrl: string | null = null

    const loadPages = async (): Promise<void> => {
      try {
        const fetchedBlobUrl = await fetchBlobUrl(imageUrl)
        if (isCancelled) {
          URL.revokeObjectURL(fetchedBlobUrl)
          return
        }
        blobUrl = fetchedBlobUrl

        const loadedPages = isPdfUrl(imageUrl)
          ? await renderPdfPages(blobUrl)
          : [await loadImagePage(blobUrl)]
        if (isCancelled) return

        if (!loadedPages) {
          setIsOverPageLimit(true)
          return
        }
        setPages(loadedPages)
      } catch (error) {
        console.error('Failed to load image:', error)
      } finally {
        if (!isCancelled) setIsImageLoaded(true)
      }
    }

    loadPages()

    return (): void => {
      isCancelled = true
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl)
      }
    }
  }, [imageUrl])

  useEffect(() => {
    if (pages.length === 0) return

    const handleSelection = (pageIndex: number, obj: fabric.FabricObject | undefined): void => {
      activePageIndexRef.current = pageIndex
      setSelectedObject(obj || null)
      if (!obj) return

      if (obj.type === 'i-text' || obj.type === 'text') {
        const textObj = obj as fabric.IText
        setTextFormat({
          fontSize: textObj.fontSize || 24,
          isBold: textObj.fontWeight === 'bold',
          isItalic: textObj.fontStyle === 'italic',
          isUnderline: textObj.underline || false,
          isStrikethrough: textObj.linethrough || false,
        })
        if (textObj.fill && typeof textObj.fill === 'string') {
          setStrokeColor(textObj.fill)
        }
      } else {
        if (obj.stroke && typeof obj.stroke === 'string') {
          setStrokeColor(obj.stroke)
        }
        if (typeof obj.strokeWidth === 'number') {
          setStrokeWidth(obj.strokeWidth)
        }
      }
    }

    const canvases: fabric.Canvas[] = []
    pages.forEach((page, pageIndex) => {
      const canvasElement = canvasElementsRef.current[pageIndex]
      if (!canvasElement) return

      const scale = width / page.pixelWidth
      const canvas = new fabric.Canvas(canvasElement, {
        width,
        height: page.pixelHeight * scale,
        selection: true,
      })

      const backgroundImage = new fabric.FabricImage(page.background)
      backgroundImage.scaleToWidth(width)
      backgroundImage.set({
        left: 0,
        top: 0,
        originX: 'left',
        originY: 'top',
      })
      canvas.backgroundImage = backgroundImage
      canvas.renderAll()

      backgroundImagesRef.current[pageIndex] = backgroundImage
      exportScalesRef.current[pageIndex] = 1 / scale
      baseSnapshotsRef.current[pageIndex] = serializeCanvas(canvas)

      canvas.on('mouse:down:before', () => {
        activePageIndexRef.current = pageIndex
        canvases.forEach((otherCanvas) => {
          if (otherCanvas !== canvas && otherCanvas.getActiveObject()) {
            otherCanvas.discardActiveObject()
            otherCanvas.renderAll()
          }
        })
      })
      canvas.on('object:added', () => saveToHistory(pageIndex))
      canvas.on('object:modified', () => saveToHistory(pageIndex))
      canvas.on('object:removed', () => saveToHistory(pageIndex))
      canvas.on('selection:created', () => handleSelection(pageIndex, canvas.getActiveObject()))
      canvas.on('selection:updated', () => handleSelection(pageIndex, canvas.getActiveObject()))
      canvas.on('selection:cleared', () => {
        if (activePageIndexRef.current === pageIndex) setSelectedObject(null)
      })

      canvases.push(canvas)
    })

    fabricCanvasesRef.current = canvases
    activePageIndexRef.current = 0
    historyRef.current = []
    historyIndexRef.current = -1
    updateHistoryButtons()
    setFabricCanvases(canvases)

    return (): void => {
      canvases.forEach((canvas) => canvas.dispose())
      fabricCanvasesRef.current = []
      backgroundImagesRef.current = []
      exportScalesRef.current = []
      baseSnapshotsRef.current = []
      setFabricCanvases([])
    }
  }, [pages, width, saveToHistory, updateHistoryButtons])

  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return

    const handleWheel = (e: WheelEvent): void => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault()
        const delta = e.deltaY > 0 ? -0.1 : 0.1
        setZoomLevel((prev) => Math.min(Math.max(prev + delta, 0.5), 3))
      }
    }

    const handleKeyDown = (e: KeyboardEvent): void => {
      if ((e.ctrlKey || e.metaKey) && (e.key === '=' || e.key === '+')) {
        e.preventDefault()
        setZoomLevel((prev) => Math.min(prev + 0.25, 3))
      } else if ((e.ctrlKey || e.metaKey) && e.key === '-') {
        e.preventDefault()
        setZoomLevel((prev) => Math.max(prev - 0.25, 0.5))
      } else if ((e.ctrlKey || e.metaKey) && e.key === '0') {
        e.preventDefault()
        setZoomLevel(1)
      }
    }

    container.addEventListener('wheel', handleWheel, { passive: false })
    window.addEventListener('keydown', handleKeyDown)

    return (): void => {
      container.removeEventListener('wheel', handleWheel)
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  useEffect(() => {
    fabricCanvases.forEach((canvas) => {
      canvas.isDrawingMode = !disabled && activeTool === 'pen'

      if (activeTool === 'pen') {
        canvas.freeDrawingBrush = new fabric.PencilBrush(canvas)
        canvas.freeDrawingBrush.color = strokeColor
        canvas.freeDrawingBrush.width = strokeWidth
      }

      canvas.selection = activeTool === 'select'
    })
  }, [fabricCanvases, activeTool, strokeColor, strokeWidth, disabled])

  const handleCanvasMouseDown = useCallback(
    (canvas: fabric.Canvas, e: fabric.TPointerEventInfo<fabric.TPointerEvent>): void => {
      if (disabled || activeTool === 'pen' || activeTool === 'select') return

      const pointer = canvas.getScenePoint(e.e)

      if (activeTool === 'rectangle') {
        const rect = new fabric.Rect({
          left: pointer.x,
          top: pointer.y,
          width: 100,
          height: 80,
          fill: 'transparent',
          stroke: strokeColor,
          strokeWidth: strokeWidth,
        })
        canvas.add(rect)
        canvas.setActiveObject(rect)
      } else if (activeTool === 'circle') {
        const circle = new fabric.Circle({
          left: pointer.x,
          top: pointer.y,
          radius: 50,
          fill: 'transparent',
          stroke: strokeColor,
          strokeWidth: strokeWidth,
        })
        canvas.add(circle)
        canvas.setActiveObject(circle)
      } else if (activeTool === 'text') {
        const text = new fabric.IText('Text', {
          left: pointer.x,
          top: pointer.y,
          fontSize: 24,
          fill: strokeColor,
          fontFamily: 'Arial',
        })
        canvas.add(text)
        canvas.setActiveObject(text)
        text.enterEditing()
        setActiveTool('select')
      }

      canvas.renderAll()
    },
    [activeTool, strokeColor, strokeWidth, disabled],
  )

  useEffect(() => {
    const mouseDownHandlers = fabricCanvases.map(
      (canvas) =>
        (e: fabric.TPointerEventInfo<fabric.TPointerEvent>): void =>
          handleCanvasMouseDown(canvas, e),
    )
    fabricCanvases.forEach((canvas, index) => canvas.on('mouse:down', mouseDownHandlers[index]))

    return (): void => {
      fabricCanvases.forEach((canvas, index) => canvas.off('mouse:down', mouseDownHandlers[index]))
    }
  }, [fabricCanvases, handleCanvasMouseDown])

  const handleDeleteSelected = useCallback((): void => {
    const canvas = getActiveCanvas()
    if (!canvas) return

    const activeObjects = canvas.getActiveObjects()
    activeObjects.forEach((obj) => {
      if (obj.selectable !== false) {
        canvas.remove(obj)
      }
    })
    canvas.discardActiveObject()
    canvas.renderAll()
  }, [getActiveCanvas])

  const handleZoomIn = useCallback((): void => {
    setZoomLevel((prev) => Math.min(prev + 0.25, 3))
  }, [])

  const handleZoomOut = useCallback((): void => {
    setZoomLevel((prev) => Math.max(prev - 0.25, 0.5))
  }, [])

  const updateActiveObject = useCallback(
    (updateObject: (activeObject: fabric.FabricObject) => void): void => {
      const canvas = getActiveCanvas()
      const activeObject = canvas?.getActiveObject()
      if (!canvas || !activeObject) return

      updateObject(activeObject)
      canvas.renderAll()
      saveToHistory(activePageIndexRef.current)
    },
    [getActiveCanvas, saveToHistory],
  )

  const updateTextProperty = useCallback(
    (property: string, value: string | number): void => {
      const isText = selectedObject?.type === 'i-text' || selectedObject?.type === 'text'
      if (!selectedObject || !isText) return

      updateActiveObject((activeObject) => activeObject.set(property, value))
    },
    [selectedObject, updateActiveObject],
  )

  const handleFontSizeChange = useCallback(
    (newSize: number): void => {
      setTextFormat((prev) => ({ ...prev, fontSize: newSize }))
      updateTextProperty('fontSize', newSize)
    },
    [updateTextProperty],
  )

  const toggleConfig = {
    bold: { stateKey: 'isBold', fabricProp: 'fontWeight', values: ['normal', 'bold'] },
    italic: { stateKey: 'isItalic', fabricProp: 'fontStyle', values: ['normal', 'italic'] },
    underline: { stateKey: 'isUnderline', fabricProp: 'underline', values: [false, true] },
    strikethrough: {
      stateKey: 'isStrikethrough',
      fabricProp: 'linethrough',
      values: [false, true],
    },
  } as const

  const handleTextFormat = useCallback(
    (type: keyof typeof toggleConfig): void => {
      const config = toggleConfig[type]
      setTextFormat((prev) => {
        const newValue = !prev[config.stateKey]
        updateTextProperty(
          config.fabricProp,
          (newValue ? config.values[1] : config.values[0]) as string | number,
        )
        return { ...prev, [config.stateKey]: newValue }
      })
    },
    [updateTextProperty],
  )

  const handleToolChange = (
    _event: React.MouseEvent<HTMLElement>,
    newTool: MarkupTool | null,
  ): void => {
    if (newTool !== null) {
      setActiveTool(newTool)
    }
  }

  const exportPage = useCallback(
    (pageIndex: number): string =>
      fabricCanvasesRef.current[pageIndex].toDataURL({
        format: 'png',
        quality: 1,
        multiplier: exportScalesRef.current[pageIndex],
        enableRetinaScaling: false,
      }),
    [],
  )

  const exportDocument = useCallback(async (): Promise<Blob | null> => {
    if (fabricCanvasesRef.current.length === 0 || pages.length === 0) return null

    if (!imageUrl || !isPdfUrl(imageUrl)) {
      return dataUrlToBlob(exportPage(0), 'image/png')
    }

    const { jsPDF } = await import('jspdf')
    const [firstPage] = pages
    const pdf = new jsPDF({
      unit: 'pt',
      format: [firstPage.pointWidth, firstPage.pointHeight],
      orientation: getOrientation(firstPage),
    })
    pages.forEach((page, pageIndex) => {
      if (pageIndex > 0) {
        pdf.addPage([page.pointWidth, page.pointHeight], getOrientation(page))
      }
      pdf.addImage(exportPage(pageIndex), 'PNG', 0, 0, page.pointWidth, page.pointHeight)
    })
    return pdf.output('blob')
  }, [exportPage, imageUrl, pages])

  const handleSave = useCallback(async (): Promise<void> => {
    if (!onSave) return

    setIsSaving(true)
    try {
      const blob = await exportDocument()
      if (blob) await onSave(blob)
    } finally {
      setIsSaving(false)
    }
  }, [exportDocument, onSave])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (disabled) return

      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault()
        if (e.shiftKey) {
          handleRedo()
        } else {
          handleUndo()
        }
      }

      if (e.key === 'Delete' || e.key === 'Backspace') {
        const activeElement = document.activeElement
        const activeObj = getActiveCanvas()?.getActiveObject()
        const isEditingText =
          activeObj && 'isEditing' in activeObj && (activeObj as fabric.IText).isEditing
        if (
          activeElement?.tagName !== 'INPUT' &&
          activeElement?.tagName !== 'TEXTAREA' &&
          !isEditingText
        ) {
          handleDeleteSelected()
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return (): void => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [disabled, handleUndo, handleRedo, handleDeleteSelected, getActiveCanvas])

  return (
    <Box
      ref={containerRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        p: 2,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        backgroundColor: theme.palette.background.paper,
        maxHeight: '650px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          alignItems: 'center',
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
          p: 1,
          minHeight: '47px',
        }}
      >
        <ToggleButtonGroup
          value={activeTool}
          exclusive
          onChange={handleToolChange}
          size='small'
          disabled={disabled}
          sx={{
            '& .MuiToggleButton-root': {
              border: 'none',
              borderRadius: 0,
              px: 1.5,
              py: 0.5,
              minWidth: 'auto',
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              },
              '&.Mui-selected': {
                backgroundColor: theme.palette.action.selected,
                '&:hover': {
                  backgroundColor: theme.palette.action.selected,
                },
              },
            },
          }}
        >
          <ToggleButton value='select' aria-label='select'>
            <Tooltip title='Select'>
              <Typography variant='caption'>Select</Typography>
            </Tooltip>
          </ToggleButton>
          <ToggleButton value='pen' aria-label='pen'>
            <Tooltip title='Pen'>
              <Icon name='EditOutlined' size={18} />
            </Tooltip>
          </ToggleButton>
          <ToggleButton value='rectangle' aria-label='rectangle'>
            <Tooltip title='Rectangle'>
              <Icon name='CropSquareOutlined' size={18} />
            </Tooltip>
          </ToggleButton>
          <ToggleButton value='circle' aria-label='circle'>
            <Tooltip title='Circle'>
              <Icon name='Circle' size={18} />
            </Tooltip>
          </ToggleButton>
          <ToggleButton value='text' aria-label='text'>
            <Tooltip title='Text'>
              <Icon name='TextFields' size={18} />
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>

        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          {COLORS.map((color) => (
            <Tooltip key={color} title={color}>
              <IconButton
                size='small'
                onClick={() => {
                  setStrokeColor(color)
                  updateActiveObject((activeObject) => {
                    if (activeObject.type === 'i-text' || activeObject.type === 'text') {
                      activeObject.set('fill', color)
                    } else {
                      activeObject.set('stroke', color)
                    }
                  })
                }}
                disabled={disabled}
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: 0.5,
                  backgroundColor: color,
                  border:
                    strokeColor === color
                      ? `2px solid ${theme.palette.primary.main}`
                      : '1px solid #ccc',
                  '&:hover': {
                    backgroundColor: color,
                    opacity: 0.8,
                  },
                }}
              />
            </Tooltip>
          ))}
        </Box>

        {activeTool === 'select' &&
          (selectedObject?.type === 'i-text' || selectedObject?.type === 'text') && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant='caption' sx={{ whiteSpace: 'nowrap' }}>
                  Font Size:
                </Typography>
                <Select
                  value={textFormat.fontSize}
                  onChange={(e) => handleFontSizeChange(Number(e.target.value))}
                  disabled={disabled}
                  size='small'
                  sx={{
                    minWidth: 70,
                    height: 28,
                    fontSize: '13px',
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                    boxShadow: `inset 0 0 0 1px ${theme.palette.text.secondary}`,
                    '&:hover': {
                      boxShadow: `inset 0 0 0 2px ${theme.palette.primary.main}`,
                    },
                    '&.Mui-focused': {
                      boxShadow: `inset 0 0 0 2px ${theme.palette.primary.main}`,
                    },
                  }}
                >
                  {[8, 10, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48, 60, 72].map((size) => (
                    <MenuItem key={size} value={size}>
                      {size}
                    </MenuItem>
                  ))}
                </Select>
              </Box>

              <Box>
                <IconButton
                  onClick={() => handleTextFormat('bold')}
                  disabled={disabled}
                  size='small'
                  sx={{
                    borderRadius: 0,
                    backgroundColor: textFormat.isBold
                      ? theme.palette.action.selected
                      : 'transparent',
                    '&:hover': {
                      backgroundColor: textFormat.isBold
                        ? theme.palette.action.selected
                        : theme.palette.action.hover,
                    },
                  }}
                >
                  <Tooltip title='Bold'>
                    <Icon name='FormatBold' size={18} />
                  </Tooltip>
                </IconButton>
                <IconButton
                  onClick={() => handleTextFormat('italic')}
                  disabled={disabled}
                  size='small'
                  sx={{
                    borderRadius: 0,
                    backgroundColor: textFormat.isItalic
                      ? theme.palette.action.selected
                      : 'transparent',
                    '&:hover': {
                      backgroundColor: textFormat.isItalic
                        ? theme.palette.action.selected
                        : theme.palette.action.hover,
                    },
                  }}
                >
                  <Tooltip title='Italic'>
                    <Icon name='FormatItalic' size={18} />
                  </Tooltip>
                </IconButton>
                <IconButton
                  onClick={() => handleTextFormat('underline')}
                  disabled={disabled}
                  size='small'
                  sx={{
                    borderRadius: 0,
                    backgroundColor: textFormat.isUnderline
                      ? theme.palette.action.selected
                      : 'transparent',
                    '&:hover': {
                      backgroundColor: textFormat.isUnderline
                        ? theme.palette.action.selected
                        : theme.palette.action.hover,
                    },
                  }}
                >
                  <Tooltip title='Underline'>
                    <Icon name='FormatUnderlined' size={18} />
                  </Tooltip>
                </IconButton>
                <IconButton
                  onClick={() => handleTextFormat('strikethrough')}
                  disabled={disabled}
                  size='small'
                  sx={{
                    borderRadius: 0,
                    backgroundColor: textFormat.isStrikethrough
                      ? theme.palette.action.selected
                      : 'transparent',
                    '&:hover': {
                      backgroundColor: textFormat.isStrikethrough
                        ? theme.palette.action.selected
                        : theme.palette.action.hover,
                    },
                  }}
                >
                  <Tooltip title='Strikethrough'>
                    <Icon name='FormatStrikethrough' size={18} />
                  </Tooltip>
                </IconButton>
              </Box>
            </>
          )}
        {(['pen', 'rectangle', 'circle'].includes(activeTool) ||
          (activeTool === 'select' &&
            selectedObject &&
            selectedObject.type !== 'i-text' &&
            selectedObject.type !== 'text')) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant='caption' sx={{ minWidth: 60 }}>
              Stroke: {strokeWidth}px
            </Typography>
            <Slider
              value={strokeWidth}
              onChange={(_, value) => {
                const newWidth = value as number
                setStrokeWidth(newWidth)
                const activeObject = getActiveCanvas()?.getActiveObject()
                if (
                  activeTool === 'select' &&
                  activeObject &&
                  !['i-text', 'text'].includes(activeObject.type || '')
                ) {
                  updateActiveObject((selected) => selected.set('strokeWidth', newWidth))
                }
              }}
              min={1}
              max={20}
              step={1}
              disabled={disabled}
              sx={{ width: 100 }}
            />
          </Box>
        )}
        <Box sx={{ flexGrow: 1 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 1 }}>
          <IconButton onClick={handleZoomOut} disabled={disabled || zoomLevel <= 0.5} size='small'>
            <Tooltip title='Zoom Out'>
              <Icon name='ZoomOut' size={18} />
            </Tooltip>
          </IconButton>
          <Typography variant='caption' sx={{ minWidth: 45, textAlign: 'center' }}>
            {Math.round(zoomLevel * 100)}%
          </Typography>
          <IconButton onClick={handleZoomIn} disabled={disabled || zoomLevel >= 3} size='small'>
            <Tooltip title='Zoom In'>
              <Icon name='ZoomIn' size={18} />
            </Tooltip>
          </IconButton>
        </Box>

        <IconButton onClick={handleUndo} disabled={disabled || !canUndo} size='small'>
          <Tooltip title='Undo'>
            <Icon name='Undo' size={18} />
          </Tooltip>
        </IconButton>
        <IconButton onClick={handleRedo} disabled={disabled || !canRedo} size='small'>
          <Tooltip title='Redo'>
            <Icon name='Redo' size={18} />
          </Tooltip>
        </IconButton>
        <IconButton onClick={handleDeleteSelected} disabled={disabled} size='small'>
          <Tooltip title='Delete Selected'>
            <Icon name='DeleteOutlined' size={18} />
          </Tooltip>
        </IconButton>
      </Box>
      <Box
        ref={scrollContainerRef}
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          overflow: 'auto',
          backgroundColor: theme.palette.background.secondary,
          position: 'relative',
          maxHeight: '500px',
          minHeight: isImageLoaded ? undefined : height,
        }}
      >
        <Box
          className='document-markup__pages'
          sx={{ transform: zoomLevel !== 1 ? `scale(${zoomLevel})` : 'none' }}
        >
          {pages.map((_page, pageIndex) => (
            <Box key={`${imageUrl}-${pageIndex}`} className='document-markup__page'>
              <canvas
                ref={(canvasElement) => {
                  canvasElementsRef.current[pageIndex] = canvasElement
                }}
              />
            </Box>
          ))}
        </Box>
        {isOverPageLimit && (
          <Typography variant='body2' className='document-markup__message'>
            Markup is only available for PDFs with up to {MAX_MARKUP_PDF_PAGES} pages.
          </Typography>
        )}
        {!isImageLoaded && imageUrl && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: theme.palette.background.secondary,
            }}
          >
            <Typography
              variant='body2'
              sx={{
                color: 'text.secondary',
              }}
            >
              Loading image...
            </Typography>
          </Box>
        )}
      </Box>
      {onSave && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant='contained'
            onClick={handleSave}
            disabled={isSaving || pages.length === 0}
          >
            Save Markup
          </Button>
        </Box>
      )}
    </Box>
  )
}

export { DocumentMarkup }
export type { DocumentMarkupProps }
export default DocumentMarkup
